import type { HubCapsule } from '@aws-amplify/core'
import { Auth, Hub } from 'aws-amplify'
import { useCallback, useEffect, useState } from 'react'

import { useLocationContext } from '@providers/location'
import { Services } from 'service-api'
import { captureException } from 'service-api/src/shared'
import { getCognitoLUPIds } from './helpers'
import { useSessionContext } from './session_context'

export const useSessionHandler = () => {
  const sessionCtx = useSessionContext()
  const locationCtx = useLocationContext()
  const [isSignUp, setIsSignUp] = useState(false)

  const createLUP = useCallback(async () => {
    const currentUser = await Auth.currentAuthenticatedUser()
    let userLocation = undefined

    // If the user was created on Admin, will have LUP but not Cognito
    //    the login/signup will create the cognito user, when call currentAuthenticatedUser will get the claims
    // if the user was created on Client, will need to create LUP and Cognito
    //    here we create the LUP and call again currentAuthenticatedUser to refresh the token and get the claims
    const userLUPs = getCognitoLUPIds(
      currentUser.username,
      currentUser.signInUserSession?.accessToken?.payload?.['cognito:groups'],
      false
    )
    userLocation = userLUPs?.find((lup) => lup.locationName === locationCtx.location?.name)

    // if the logged in user does not have a LUP for the current location
    // we need to create one
    if (!userLocation) {
      try {
        await Services.ClientLocationUserProfileService.post(locationCtx.location?.name || '', {
          email: currentUser.username,
          locationId: locationCtx.location?.id || '',
        })
        // we have a LUP, now need to force to refresh the token to get the new LUP data
        return await Auth.currentAuthenticatedUser({ bypassCache: true })
      } catch (error) {
        captureException(error as Error, {
          context: {
            email: sessionCtx.state.user?.email || 'n/a',
            locationName: locationCtx.location?.name || 'n/a',
          },
        })
        return null
      }
    }
    return currentUser
  }, [locationCtx.location?.id, locationCtx.location?.name, sessionCtx.state.user?.email])

  useEffect(() => {
    if (locationCtx.isLoading) {
      return
    }
    let cancel = false
    const authHandler = async (data: HubCapsule) => {
      if (cancel) return
      switch (data.payload.event) {
        // when the user register for the first time, the app is setup to auto-signin
        // we'll receive a confirmSignUp event, and then a signIn event
        case 'confirmSignUp':
          setIsSignUp(true)
          break
        case 'signIn':
          // if is auto-signin after the signup, we need to create the LUP
          // before let the user to continue
          if (isSignUp) {
            const forceUser = await createLUP()
            if (!forceUser) {
              sessionCtx.dispatch({ type: 'SIGNOUT' })
              return
            }
            sessionCtx.dispatch({
              type: 'AUTHENTICATE',
              payload: { user: forceUser, currentLocation: locationCtx.location?.name || '' },
            })
          } else {
            const newLup = await createLUP()
            sessionCtx.dispatch({
              type: 'AUTHENTICATE',
              payload: { user: newLup ?? data.payload.data, currentLocation: locationCtx.location?.name || '' },
            })
          }
          break
        case 'signOut':
          sessionCtx.dispatch({ type: 'DEAUTHENTICATE' })
          break
      }
    }
    const removeListener = Hub.listen('auth', authHandler)

    return () => {
      cancel = true
      removeListener()
    }
  })
}
